import { ArrowElbowDownLeft, TextAlignLeft } from "@phosphor-icons/react";
import { useRef, useState } from "react";

import RenderIf from "common/components/RenderIf";
import { Input } from "common/components/ui/Input";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { KeywordTypes, ProfileSearchParams } from "common/types";

import Keyword from "./Keyword";
import KeywordTypeSelect from "./KeywordTypeSelect";
import useKeywords from "./useKeywords";

export default function Keywords({
  keywords,
  setSearchParams,
}: {
  keywords: ProfileSearchParams["keywords"];
  setSearchParams: (searchParams: Partial<ProfileSearchParams>) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedKeywordType, setSelectedKeywordType] =
    useState<KeywordTypes>("general");
  const isDesktop = useTwBreakpoint("lg");

  const { addKeyword } = useKeywords(keywords, setSearchParams);

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && !!e.currentTarget.value.trim()) {
      addKeyword(e.currentTarget.value.trim(), selectedKeywordType);

      inputRef.current.value = "";
    }
  }

  const isLinkedInQuerySearch = keywords.some(
    ({ modifier }) => modifier === "raw",
  );

  const selectedTypeKeywords = keywords.filter(
    (keyword) => keyword.type === selectedKeywordType,
  );
  // Each keyword type has a limit of 6 keywords
  const isKeywordsLimitReached = selectedTypeKeywords.length === 6;

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2">
        <Input
          id="keywordsInput"
          ref={inputRef}
          className="flex-1"
          variant={isDesktop ? "sm" : "lg"}
          onKeyDown={onKeyDown}
          leftComponent={<TextAlignLeft />}
          rightComponent={
            <RenderIf condition={isDesktop && !isLinkedInQuerySearch}>
              <div className="flex items-center gap-1 text-black-400">
                <ArrowElbowDownLeft className="fill-black-400" />
                <span className="text-nowrap">
                  Press &apos;Enter&apos; to add
                </span>
              </div>
            </RenderIf>
          }
          placeholder={
            isLinkedInQuerySearch
              ? "Cannot add keywords when using LinkedIn URL Search"
              : "Enter keyword here"
          }
          disabled={isKeywordsLimitReached || isLinkedInQuerySearch}
        />
        <KeywordTypeSelect
          selectedKeywordType={selectedKeywordType}
          setSelectedKeywordType={setSelectedKeywordType}
          isDisabled={isLinkedInQuerySearch}
          size={isDesktop ? "sm" : "lg"}
        />
      </div>

      <RenderIf condition={isKeywordsLimitReached}>
        <p className="mt-1 pl-1 text-xxs text-black-400">
          You&apos;ve added the maximum number of keywords (6) for this keyword
          type
        </p>
      </RenderIf>

      {/* List of keywords */}
      <RenderIf condition={!!keywords.length}>
        <div className="flex flex-wrap gap-2 mt-1.5">
          {keywords.map((keyword) => (
            <Keyword
              key={`${keyword.text}-${keyword.modifier}-${keyword.type}`}
              keyword={keyword}
              keywords={keywords}
              setSearchParams={setSearchParams}
            />
          ))}
        </div>
      </RenderIf>
    </div>
  );
}
